import { Badge, Skeleton } from '@round/creatorbase-ui';
import { CopyButton } from '@round/creatorbase-helpers/components';
import cn from 'classnames';

import { platforms, PlatformIcon } from 'Modules/Plans/helpers';

import { ClientInfluencerAccount } from '../helpers';

import styles from './Accounts.module.css';
import { buildInstagramUserUrl, buildTiktokUserUrl } from 'helpers';

const skeletonPlatformSlots = platforms.filter((platform) => platform !== 'youtube');

type Props = {
    accounts: ClientInfluencerAccount[];
    activeAccountId: number | undefined;
    onSelectAccount: (accountId: number) => void;
    isLoading?: boolean;
};

const Accounts = ({ accounts, activeAccountId, onSelectAccount, isLoading }: Props) => {
    if (isLoading) {
        return (
            <div className={styles.list} aria-busy="true" aria-label="Loading accounts">
                {skeletonPlatformSlots.map((platform) => (
                    <Skeleton
                        key={`account-skeleton-${platform}`}
                        className={cn(styles.accountBadge, styles.badgeSkeleton)}
                    />
                ))}
            </div>
        );
    }

    const orderedAccounts = platforms
        .map((platform) => accounts.find((account) => account.platform === platform))
        .filter((account): account is ClientInfluencerAccount => account != null);

    if (!orderedAccounts.length) {
        return null;
    }

    return (
        <div className={styles.list}>
            {orderedAccounts.map((account) => {
                const isActive = account.creatorbase_client_influencer_account_id === activeAccountId;
                const handleContent = (
                    <>
                        <PlatformIcon
                            className={styles.platformIcon}
                            aria-hidden
                            platform={account.platform}
                            isSolid={isActive}
                        />
                        @{account.platform_username}
                    </>
                );
                const platformUrl =
                    account.platform === 'tiktok'
                        ? buildTiktokUserUrl(account.platform_username)
                        : buildInstagramUserUrl(account.platform_username);

                // Badge is a plain container here (not asChild) so the handle and the copy
                // button can sit as sibling segments inside one pill. The handle mirrors the
                // platform tabs below: an inactive badge selects that platform, while clicking
                // the already-active badge opens the account in a new tab.
                return (
                    <Badge
                        key={account.creatorbase_client_influencer_account_id}
                        variant={isActive ? 'default' : 'secondary'}
                        className={styles.accountBadge}
                    >
                        {isActive ? (
                            <a
                                className={cn(styles.handle, styles.handleLink)}
                                href={platformUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {handleContent}
                            </a>
                        ) : (
                            <button
                                className={styles.handle}
                                type="button"
                                onClick={() => onSelectAccount(account.creatorbase_client_influencer_account_id)}
                            >
                                {handleContent}
                            </button>
                        )}

                        <CopyButton
                            value={account.platform_username}
                            tooltipLabel="Copy account handle"
                            variant="ghost"
                            size="icon-xs"
                            className={styles.copyButton}
                        />
                    </Badge>
                );
            })}
        </div>
    );
};

export default Accounts;
